import sweetAlertService from 'utils/sweetAlertService';
import apiService from './apiService';
import { InsType } from 'constants/enums/insType.enum';
import commonService from 'utils/commonService';
import { Products, EvalReq, EvalRes, PolicyFeeReq, PolicyFee, PolicyNoReq, PolicyNo, CheckApplyTypeReq, CheckApplyType, RuleValidReq, RuleValid, AgeLimitcnds, AgeLimitcnd, ApplyFormReq, PdfExportContent, ApplyForm, PolicyAddEnListReq, PaymentNccReq, PaymentNccRes, PaymentEBillReq, PaymentEBillRes, PolicyAddReq } from 'bff/models/product';
import { BaseRes } from 'bff/models/base/baseRes';
import {
  TravelWarnings,
  KycClaims,
  SysOptionReq,
  AmtOptReq,
  AmtOpt,
  Activities,
  Occupations,
  Activity,
  KycClaim,
  Postcodes,
  Codes,
  FlightScheduleReq,
  FlightSchedules,
  EmailDomains,
  InsuredAmtOptReq,
  InsuredAmtOpt,
  Inscats, Inscat,
  FlightInfoReq,
  AgeData
} from 'bff/models/info';
import { InsureProcess } from 'constants/models/components/insurProcess/insureProcess';
import { SysOptions } from 'bff/models/info/sysoptions';
import { InsCount } from 'constants/enums/insCount.enum';
import { ReturnCode } from 'constants/enums/returnCode.enum';
import { LinkCategory } from 'constants/enums/linkCategory.enum';
import { TravelDoc, Links } from 'bff/models/link';
import { CodeClass } from 'constants/enums/codeClass.enum';
import _ from 'lodash';
import { PaymentMethod } from 'bff/models/product/paymentMethod';
import { SweetAlertIcon } from 'constants/enums/sweetAlertIcon.enum';
import axios from 'axios';
import { divideDate } from 'utils/helpers';
import { Limit } from 'constants/enums/limit.enum';
import { SysOptType } from 'constants/enums/sysOptType.enum';
import { ActualAge, ActualAgeData } from 'bff/models/info/actualAge';

/**
 * @description 取得暫存的投保作業資料
*/
const fetchTempState = async () => {
  const res = await apiService.fetchInsureProcessTempState();
  return { ...res };
};

/**
 * @description 取得旅平險投保作業頁初始化資料
 * @param insTypeId 險種類別
 */
const fetchInitSetting = async (insTypeId: string) => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig();

  const result: InsureProcess = await Promise.all([
    apiService.fetchInscat(baseReq, reqConfig),
    apiService.fetchProductByInsTypeId(baseReq, reqConfig, insTypeId),
    apiService.fetchCodes(baseReq, reqConfig, CodeClass.UI),
    apiService.fetchTravelwarning(baseReq, reqConfig)
  ]).then(([inscatsRes, prodsRes, UICodeRes, travelwarningRes]) => {
    const inscatsBaseRes: BaseRes<Inscats> = { ...inscatsRes };
    const inscatRes = _.find(inscatsBaseRes.TRANRS.INSURANCE_CATEGORY, (inscat: Inscat) => inscat.INS_TYPE_ID === insTypeId) as Inscat;
    const prodsBaseRes: BaseRes<Products> = { ...prodsRes };
    const UICodeBaseRes: BaseRes<Codes> = { ...UICodeRes };
    const travelWarningBaseRes: BaseRes<TravelWarnings> = { ...travelwarningRes };

    const res: InsureProcess = {
      inscat: { ...inscatRes },
      products: prodsBaseRes.TRANRS,
      UICode: commonService.arrayTypeReturn(UICodeBaseRes.TRANRS.CODES),
      travelWarnings: commonService.arrayTypeReturn(travelWarningBaseRes.TRANRS.GEOCOUNTRYS)
    };

    return res;
  });

  return result;
};

/**
 * @description 取得保險人數對應的保險對象
*/
const fetchSubjectCode = async (PClass: SysOptType) => {
  const sysOptionReq: SysOptionReq = {
    PCLASS: [PClass]
  };
  const baseReq = commonService.getApiReqData(sysOptionReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchSysOptions(baseReq, reqConfig).then((res: BaseRes<SysOptions>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal('無法取得保險對象', res.MWHEADER.RETURNCODE);
  } else {
    return [...res.TRANRS[PClass]];
  }
};

/**
 * @description 取得身故失能保額選項清單
 * @param insTypeId 險種代號
 * @param prodId 商品代號
*/
const fetchAmtOpt = async (insTypeId: InsType, prodId: string) => {
  const amtOptReq: AmtOptReq = {
    INS_TYPE_ID: insTypeId,
    PROD_ID: prodId
  };

  const baseReq = commonService.getApiReqData(amtOptReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchAmtOpt(baseReq, reqConfig).then((res: BaseRes<AmtOpt>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal('無法取得身故失能保額', res.MWHEADER.RETURNCODE);
  } else {
    return { ...res.TRANRS };
  }
};

/**
 * @description 保費試算
*/
const evalProd = async (evalReq: EvalReq) => {
  const baseReq = commonService.getApiReqData(evalReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.evalProd(baseReq, reqConfig).then((res: BaseRes<EvalRes>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE, '');
  }

  return res.TRANRS;
};

/**
 * @description 取得 填寫資料 Page 1 初始化資料
*/
const fetchInitProcessFill1st = async () => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig();

  const result = await Promise.all([
    apiService.fetchTravelwarning(baseReq, reqConfig),
    apiService.fetchLinkByCategory(baseReq, reqConfig, LinkCategory.TravelDoc),
    apiService.fetchCodes(baseReq, reqConfig, CodeClass.QUICKCLAIM)
  ]).then(([
    travelwarningRes,
    docLinksRes,
    quickClaimRes
  ]) => {
    const travelWarningBaseRes: BaseRes<TravelWarnings> = { ...travelwarningRes };
    const docLinksBaseRes: BaseRes<Links<TravelDoc>> = { ...docLinksRes };
    const quickClaimBaseRes: BaseRes<Codes> = { ...quickClaimRes };

    const res = {
      travelWarnings: commonService.arrayTypeReturn(travelWarningBaseRes.TRANRS.GEOCOUNTRYS),
      docLinks: commonService.arrayTypeReturn(docLinksBaseRes.TRANRS.LINKS?.TRAVELDOC),
      quickClaimCode: commonService.arrayTypeReturn(quickClaimBaseRes.TRANRS.CODES)
    };

    return res;
  });

  return result;
};

/**
 * @description 取得 填寫資料 Page 2 初始化資料
*/
const fetchInitProcessFill2nd = async (insTypeId: string, prodId: string) => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig();

  const result = await Promise.all([
    apiService.fetchKycClaim(baseReq, reqConfig),
    apiService.fetchOccupation(baseReq, reqConfig),
    apiService.fetchPostcode(baseReq, reqConfig),
    apiService.fetchEmailDN(baseReq, reqConfig),
    apiService.fetchAgeLimitcndByInsType(baseReq, reqConfig, insTypeId)
  ]).then(([kycClaimsRes, occupationRes, postcodesRes, emailDomainsRes, ageLimitcndsRes]) => {
    const kycClaimsBaseRes: BaseRes<KycClaims> = { ...kycClaimsRes };
    const occupationBaseRes: BaseRes<Occupations> = { ...occupationRes };
    const postcodesBaseRes: BaseRes<Postcodes> = { ...postcodesRes };
    const emailDomainsBaseRes: BaseRes<EmailDomains> = { ...emailDomainsRes };
    const ageLimitcndsBaseRes: BaseRes<AgeLimitcnds> = { ...ageLimitcndsRes };

    const res = {
      kycClaims: [...kycClaimsBaseRes.TRANRS.KYCLIST.sort((first: KycClaim, second: KycClaim) => first.KYC_ORDER - second.KYC_ORDER)],
      occupations: commonService.filterOccupationOpts(commonService.arrayTypeReturn(occupationBaseRes.TRANRS.OCCUPATION)),
      postcodes: commonService.arrayTypeReturn(postcodesBaseRes.TRANRS.POSTCODE),
      emailDomains: commonService.arrayTypeReturn(emailDomainsBaseRes.TRANRS.EMAILDN),
      ageLimitcnd: _.find(commonService.arrayTypeReturn(ageLimitcndsBaseRes.TRANRS.AGELIMIT), (ageLimitcnd: AgeLimitcnd) => ageLimitcnd.PROD_ID === prodId)
    };

    return res;
  });

  return result;
};

/**
 * @description 初始化 成功投保頁
 * @param insTypeId 險種類別
*/
const fetchInitProcessSuccess = async (insTypeId: string) => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig();

  const result = await Promise.all([
    apiService.fetchActivitiesByInsTypeId(baseReq, reqConfig, insTypeId),
    apiService.fetchCodes(baseReq, reqConfig, CodeClass.INS_DONE_BAR),
    apiService.fetchCodes(baseReq, reqConfig, CodeClass.INS_DONE)
  ]).then(([activitiesRes, insDoneBarRes, insDoneRes]) => {
    const activitiesBaseRes: BaseRes<Activities> = { ...activitiesRes };
    const insDoneBaseRes: BaseRes<Codes> = { ...insDoneRes };
    const insDoneBarBaseRes: BaseRes<Codes> = { ...insDoneBarRes };

    const res = {
      activities: {
        MORE_ACTIVITY_URL: activitiesBaseRes.TRANRS.MORE_ACTIVITY_URL ? activitiesBaseRes.TRANRS.MORE_ACTIVITY_URL : '',
        ACTIVITY: activitiesBaseRes.TRANRS.ACTIVITY ? [...activitiesBaseRes.TRANRS.ACTIVITY.sort((first: Activity, second: Activity) => +first.SHOW_ORDER - +second.SHOW_ORDER)] : new Array(0)
      },
      insDoneCode: commonService.arrayTypeReturn(insDoneBaseRes.TRANRS.CODES),
      insDoneBarCode: commonService.arrayTypeReturn(insDoneBarBaseRes.TRANRS.CODES)
    };

    return res;
  });

  return result;
};

/**
 * @description 取得航班資訊
 * @param flightDate 航班日期
 * @param carrierId 航班公司代碼
 * @param flightNo 航班編號
 */
const fetchFlightSchedule = async (flightDate: string, carrierId: string, flightNo: string) => {
  // console.log("🚀 ~ fetchFlightSchedule ~ flightDate:", flightDate)
  const reqConfig = commonService.getReqConfig();

  const { y, m, d } = divideDate(flightDate);
  const testReq: FlightInfoReq = {
    cr: carrierId,
    fn: flightNo,
    y,
    m,
    d,
    systemId: 'B2C',
    buOrigin: ''
  };
  const baseReq = commonService.getApiReqData(testReq);
  const res: any = await apiService.fetchFlightSchedule(baseReq, reqConfig);

  console.warn('🚀 ~ fetchFlightSchedule ~ res:', res);
  // console.warn('🚀 ~ fetchFlightSchedule ~ res.data:', res.data);

  return res.TRANRS;
  // switch (res.MWHEADER.RETURNCODE) {
  //   case ReturnCode.CodeSuccess:
  //     return res;
  //   case ReturnCode.CodeSuccess:
  //     return [...res.TRANRS.FLIGHT];
  //   default:
  //     sweetAlertService.modal({
  //       icon: SweetAlertIcon.Info,
  //       html: `無此航班資訊<br />(error code : ${res.MWHEADER.RETURNCODE})`
  //     });
  //     return res.TRANRS;
  // }
};

/**
 * @description 取得被保險人保額選項清單
*/
const fetchInsuredAmtOpt = async (insTypeId: string, prodId: string, planId: string, birthday: string, contractDate: string) => {
  const insuredAmtOptReq: InsuredAmtOptReq = {
    INS_TYPE_ID: insTypeId,
    PROD_ID: prodId,
    PLAN_ID: planId,
    INSURED_BIRTHDAY: birthday,
    CONTRACT_DATE: contractDate
  };
  const baseReq = commonService.getApiReqData(insuredAmtOptReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchInsuredAmtOpt(baseReq, reqConfig).then((res: BaseRes<InsuredAmtOpt>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return { ...res.TRANRS };
  }
};

/**
 * @description 取得保費計算結果-被保險人
*/
const fetchPolocyFee = async (policyFeeReq: PolicyFeeReq) => {
  const baseReq = commonService.getApiReqData(policyFeeReq);
  const reqConfig = commonService.getReqConfig();

  const res: BaseRes<PolicyFee> = await apiService.fetchPolicyFee(baseReq, reqConfig).then((res: BaseRes<PolicyFee>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
    return res.MWHEADER.RETURNDESC;
  } else {
    return { ...res.TRANRS };
  }
};

/**
 * @description 取得投保單號
 * @param prodId 商品代號
 * @param serialType A-網路要保號 P-網路投保單號
 * @param insuredType 單號類別 B-要保 C-投保
 * @param branchId 公司代碼
*/
const fetchPolicyNo = async (prodId: string, serialType: string, insuredType: string, branchId?: string) => {
  const policyNoReq: PolicyNoReq = {
    PROD_ID: prodId,
    SERIAL_TYPE: serialType,
    BC_TYPE: insuredType,
    BRANCH_ID: branchId
  };

  const baseReq = commonService.getApiReqData(policyNoReq);
  const reqConfig = commonService.getReqConfig();

  const res: BaseRes<PolicyNo> = await apiService.fetchPolicyNo(baseReq, reqConfig).then((res: BaseRes<PolicyNo>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    let desc = res.MWHEADER.RETURNDESC;
    if (/(,)(.*)/.test(desc)) {
      desc = desc.replace(/(,)(.*)/, '$2');
    }

    if (/(.*)(,)/.test(desc)) {
      desc = desc.replace(/(.*)(,)/, '$1');
    }
    commonService.showErrorModal(desc, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS.SERIALNO;
  }
};

/**
 * @description 初始化確認付款頁資料
*/
const fetchInitPayment = async (serialNo: string, insTypeId: string) => {
  const baseReq = commonService.getApiReqData({ APPLY_NO: serialNo, INS_TYPE_ID: insTypeId });
  const reqConfig = commonService.getReqConfig();

  const result = await Promise.all([
    apiService.fetchPaymentMethod(baseReq, reqConfig),
    apiService.fetchCodes(baseReq, reqConfig, CodeClass.BARCODE_NOTE),
    apiService.fetchCodes(baseReq, reqConfig, CodeClass.BARCODE_NOTICE)
  ]).then(([paymentMethodRes, barcodeNoteCodeRes, barcodeNoticeCodeRes]) => {
    const paymentBaseRes: BaseRes<PaymentMethod> = { ...paymentMethodRes };
    const barcodeNoteCodeBaseRes: BaseRes<Codes> = { ...barcodeNoteCodeRes };
    const barcodeNoticeCodeBaseRes: BaseRes<Codes> = { ...barcodeNoticeCodeRes };

    const res = {
      paymentMethod: { ...paymentBaseRes.TRANRS },
      barcodeNoteCodes: commonService.arrayTypeReturn(barcodeNoteCodeBaseRes.TRANRS.CODES),
      barcodeNoticeCodes: commonService.arrayTypeReturn(barcodeNoticeCodeBaseRes.TRANRS.CODES)
    };

    return res;
  });

  return result;
};

/**
 * @description 網要網投判斷
*/
const validApplyType = async (checkApplyTypeReq: CheckApplyTypeReq, insTypeId: string) => {
  const baseReq = commonService.getApiReqData(checkApplyTypeReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.validApplyType(baseReq, reqConfig, insTypeId).then((res: BaseRes<CheckApplyType>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    let desc = res.MWHEADER.RETURNDESC;
    if (/(,)(.*)/.test(desc)) {
      desc = desc.replace(/(,)(.*)/, '$2');
    }

    if (/(.*)(,)/.test(desc)) {
      desc = desc.replace(/(.*)(,)/, '$1');
    }
    commonService.showErrorModal(desc, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS.BC_TYPE;
  }
};

/**
 * @description 保單規則檢核
*/
const policyRuleCheck = async (ruleValidReq: RuleValidReq) => {
  const baseReq = commonService.getApiReqData(ruleValidReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.policyRuleValid(baseReq, reqConfig).then((res: BaseRes<RuleValid>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    if (res.TRANRS && res.TRANRS.ERROR_MSG) {
      return res.TRANRS;
    }

    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS;
  }
};

/**
 * @description 新增保單資料
*/
const addPolicy = async (insTypeId: string, policyAddReq: PolicyAddReq) => {
  const baseReq = commonService.getApiReqData(policyAddReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.addPolicy(baseReq, reqConfig, insTypeId).then((res: BaseRes<any>) => res);
  switch (res.MWHEADER.RETURNCODE) {
    case ReturnCode.CodeSuccess:
    case ReturnCode.CodePassApplyNo:
      return res.MWHEADER.RETURNDESC;
    default:
      commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE, '新增保單資料失敗');
      break;
  }
  // if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
  //   sweetAlertService.modal({
  //     icon: SweetAlertIcon.Info,
  //     title: '新增保單資料失敗',
  //     html: `${res.MWHEADER.RETURNDESC}<br />(error code : ${res.MWHEADER.RETURNCODE})`
  //   });
  // } else {
  //   return res.MWHEADER.RETURNDESC;
  // }
};

/**
 * @description 文件套印
*/
const downloadApplyForm = async (applyFormReqContent: PdfExportContent) => {
  const applyFormReq: ApplyFormReq = {
    PDF_EXPORT: [applyFormReqContent]
  };

  const baseReq = commonService.getApiReqData(applyFormReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.downloadApplyForm(baseReq, reqConfig).then((res: BaseRes<ApplyForm>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE, '取得檔案位置失敗');
  } else {
    return res.TRANRS.FILE_URL;
  }
};

/**
 * @description 新增英文投保證明名冊
*/
const addPolicyEnList = async (addEnListReq: PolicyAddEnListReq) => {
  const baseReq = commonService.getApiReqData(addEnListReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.policyAddEnList(baseReq, reqConfig).then((res: BaseRes<any>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE, '新增英文投保證明名冊失敗');
    return res.MWHEADER.RETURNCODE;
  } else {
    return res.MWHEADER.RETURNCODE;
  }
};

/**
 * @description 執行線上付款-信用卡
*/
const paymentNccc = async (paymentNccReq: PaymentNccReq) => {
  const baseReq = commonService.getApiReqData(paymentNccReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.paymentNccc(baseReq, reqConfig).then((res: BaseRes<PaymentNccRes>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    const htmlStr = res.TRANRS && res.TRANRS.PAYMENT
      ? `${res.TRANRS.PAYMENT.RESPONSECODE} ${res.TRANRS.PAYMENT.RESPONSEMSG}`
      : `${res.MWHEADER.RETURNDESC}<br />(error code : ${res.MWHEADER.RETURNCODE})`;

    sweetAlertService.modal({
      icon: SweetAlertIcon.Info,
      html: htmlStr
    });
  } else {
    return res.MWHEADER.RETURNCODE;
  }
};

/**
 * @description 執行線上付款-活期帳戶
*/
const paymentEBill = async (paymentEBillReq: PaymentEBillReq) => {
  const baseReq = commonService.getApiReqData(paymentEBillReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.paymentEBill(baseReq, reqConfig).then((res: BaseRes<PaymentEBillRes>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    sweetAlertService.modal({
      icon: SweetAlertIcon.Info,
      html: `${res.MWHEADER.RETURNDESC}<br />(error code : ${res.MWHEADER.RETURNCODE})`
    });
  } else {
    return res.MWHEADER.RETURNCODE;
  }
};

/**
 * @description 取得繳費條碼頁面 URL
 */
const fetchBarcodeUrl = async (applyNo: string, payerName: string) => {
  const baseReq = commonService.getApiReqData({ APPLY_NO: applyNo, PAYER_NAME: payerName });
  const reqConfig = commonService.getReqConfig();
  const res = await apiService.fetchBarcodeUrl(baseReq, reqConfig).then((res: BaseRes<any>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS.BARCODE_URL;
  }
};

/**
 * @description 取得說明 Modal 資料
 */
const fetchExplainModalData = async (insTypeID: InsType, limitID: Limit) => {
  const baseReq = commonService.getApiReqData({ INS_TYPE_ID: insTypeID, LIMIT_ID: limitID });
  const reqConfig = commonService.getReqConfig();
  const res = await apiService.fetchExplainModalData(baseReq, reqConfig).then((res: BaseRes<any>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS.TRAVEL_RESULT;
  }
};

/**
 * @description 取得實際年齡
 */
const fetchActualAge = async (ageData: ActualAgeData[]) => {
  const baseReq = commonService.getApiReqData({ AGE_DATA: ageData });
  const reqConfig = commonService.getReqConfig();
  const res = await apiService.fetchActualAge(baseReq, reqConfig).then((res: BaseRes<ActualAge>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return [...res.TRANRS.AGE_DATA];
  }
};

export default {
  fetchTempState,
  fetchInitSetting,
  fetchInitProcessFill1st,
  fetchInitProcessFill2nd,
  fetchInitProcessSuccess,
  fetchSubjectCode,
  fetchAmtOpt,
  fetchFlightSchedule,
  evalProd,
  fetchInsuredAmtOpt,
  fetchPolocyFee,
  fetchPolicyNo,
  fetchInitPayment,
  validApplyType,
  policyRuleCheck,
  addPolicy,
  downloadApplyForm,
  addPolicyEnList,
  paymentNccc,
  paymentEBill,
  fetchBarcodeUrl,
  fetchExplainModalData,
  fetchActualAge
};
